<template>
  <div class="earworm-builder-container">
    <Header :title="isEditing ? 'Edit Earworm' : 'New Earworm'" :user="currentUser" />
    
    <!-- Camera overlay -->
    <div v-if="cameraOpen" class="camera-overlay">
      <div class="camera-modal" @click.stop>
        <div class="camera-preview-container">
          <video ref="videoEl" autoplay playsinline class="camera-video" />
          <div class="document-frame">
            <div class="document-corners">
              <div class="corner top-left"></div>
              <div class="corner top-right"></div>
              <div class="corner bottom-left"></div>
              <div class="corner bottom-right"></div>
            </div>
            <div class="document-guide-text">Position document within frame</div>
          </div>
        </div>
        <div class="camera-actions">
          <button type="button" @click="capturePhoto" class="action-btn capture-btn">📸 Capture Document</button>
          <button type="button" @click="closeCamera" class="action-btn cancel-btn">Cancel</button>
        </div>
      </div>
    </div>
    
    <main class="page-main">
      <form @submit.prevent="saveEarworm" class="earworm-form">
        <!-- Earworm Name Section -->
        <div class="earworm-name-section">
          <h3>Earworm Name</h3>
          <div class="form-group name-form-group">
            <label for="earworm-name">Name your earworm:</label>
            <input 
              id="earworm-name"
              v-model="earworm.name" 
              type="text" 
              class="form-input"
              placeholder="Enter a name for this earworm..."
              maxlength="100"
            />
          </div>
        </div>

        <div class="earworm-upload-section">
          <div class="section-header upload-section-header">
            <h3>Upload Worksheet</h3>
            <div v-if="uploadedImageUrl" class="small-image-preview uploaded-image-preview">
              <img 
                :src="uploadedImageUrl" 
                alt="Preview" 
                @error="imageError = true" 
                @click="showImageModal = true"
                class="small-preview-image clickable"
                title="Click to view full size"
              />
            </div>
          </div>
          <div class="form-group upload-form-group">
            <div class="image-upload-section upload-area">
              <div class="image-upload-buttons upload-buttons-row">
                <button type="button" @click="openCamera" class="upload-btn camera-btn">
                  Camera
                </button>
                <button type="button" @click="openGallery" class="upload-btn gallery-btn">
                  Gallery
                </button>
                <button 
                  v-if="uploadedImageUrl"
                  @click="deletePhoto" 
                  class="upload-btn delete-btn"
                  type="button"
                >
                  Delete
                </button>
                <input 
                  ref="fileInput" 
                  type="file" 
                  accept="image/*"
                  @change="handleFileSelect" 
                  style="display: none;"
                />
              </div>
            </div>
            <div v-if="imageUploading" class="upload-status uploading-indicator">
              📤 Uploading image...
            </div>
          </div>
        </div>



        <!-- Build Earworm Button: moved up, label removed -->

        <div class="earworm-builder-build-row build-row-layout" style="display: flex; justify-content: flex-start; margin-bottom: 1.5rem;">
          <button 
            type="button" 
            @click="buildEarworm" 
            class="btn-build-earworm build-earworm-btn"
            :disabled="!uploadedImageUrl"
          >
            🏗️ Build Earworm
          </button>
        </div>

        <!-- Status display is only from polled earworm.status, not local state -->
        <div class="earworm-status-section latest-status-row" style="margin-bottom: 1.5rem;">
          <template v-if="earworm.status && typeof earworm.status === 'object' && earworm.status.lastevent">
            <div class="latest-status-label"><strong>Latest Status:</strong></div>
            <div class="latest-status-text">{{ earworm.status.lastevent.text }}</div>
            <div class="latest-status-time" v-if="earworm.status.lastevent.time">
              <small>{{ new Date(earworm.status.lastevent.time).toLocaleString() }}</small>
            </div>
          </template>
          <template v-else-if="earworm.status">
            <div class="latest-status-label"><strong>Latest Status:</strong></div>
            <div class="latest-status-text">{{ earworm.status }}</div>
          </template>
        </div>

        <div class="earworm-form-actions">
          <button type="button" @click="goBack" class="btn-secondary">Cancel</button>
          <button type="submit" :disabled="saving" class="btn-primary">
            {{ saving ? 'Saving...' : (isEditing ? 'Save' : 'Create Earworm') }}
          </button>
          <button 
            v-if="isEditing" 
            type="button" 
            @click="deleteEarworm" 
            class="btn-danger"
          >
            Delete
          </button>
          <button
            v-if="earworm.suno_ts_lyrics || earworm.suno_ts_lyrics2"
            type="button"
            @click="showBothTimestampedLyrics = true"
            class="btn-view-timestamped-lyrics"
            style="margin-left: 1rem;"
          >
            ⏰ Show Both Timestamped Lyrics
          </button>
          <!-- ADD: Button to open modal for copying timestamped lyrics -->
          <button
           v-if="earworm.suno_ts_lyrics || earworm.suno_ts_lyrics2"
            type="button"
            @click="showCopyTimestampedLyricsModal = true"
            class="btn-copy-timestamped-lyrics"
            style="margin-left: 1rem;"
          >
            📋 Copy Timestamped Lyrics
          </button>
        </div>
      </form>
    </main>
    
    <!-- Task Details Display moved outside form but still in main -->
    <div v-if="taskDetails" class="task-details-display">
      <h3>Task Details</h3>
      <p><strong>Status:</strong> {{ taskDetails.status }}</p>
      <p><strong>Task ID:</strong> {{ earworm.suno_task_id }}</p>
      
      <!-- Display multiple songs if they exist -->
      <div v-if="taskDetails.response?.data?.response?.sunoData" class="songs-list">
        <h4>Generated Songs ({{ taskDetails.response.data.response.sunoData.length }})</h4>
        <div 
          v-for="(song, index) in taskDetails.response.data.response.sunoData" 
          :key="index"
          class="song-item"
        >
          <div class="song-header">
            <h5>Song {{ index + 1 }}: {{ song.title || 'Untitled' }}</h5>
            <span class="song-duration">{{ song.duration }}s</span>
          </div>
          
          <div class="song-urls">
            <div v-if="song.audioUrl" class="url-item">
              <label>Audio URL:</label>
              <div class="url-controls">
                <input 
                  :value="song.audioUrl" 
                  readonly 
                  class="url-input"
                  :id="`audio-url-${index}`"
                />
                <button @click="copyToClipboard(song.audioUrl, `audio-url-${index}`)">Copy</button>
                <button @click="playAudio(song.audioUrl)">Play</button>
              </div>
            </div>
            
            <div v-if="song.imageUrl" class="url-item">
              <label>Image URL:</label>
              <div class="url-controls">
                <input 
                  :value="song.imageUrl" 
                  readonly 
                  class="url-input"
                  :id="`image-url-${index}`"
                />
                <button @click="copyToClipboard(song.imageUrl, `image-url-${index}`)">Copy</button>
                <button @click="openInNewTab(song.imageUrl)">View</button>
              </div>
            </div>
          </div>
          
          <div v-if="song.imageUrl" class="song-image">
            <img :src="song.imageUrl" :alt="`Song ${index + 1} cover`" />
          </div>
        </div>
      </div>
      
      <!-- Raw JSON display for debugging -->
      <details class="raw-data">
        <summary>Raw Response Data</summary>
        <pre>{{ JSON.stringify(taskDetails, null, 2) }}</pre>
      </details>
    </div>
    
    <!-- Analysis Results Modal -->
    <div v-if="showAnalysisModal" class="modal-overlay" @click="closeAnalysisModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ analysisError ? 'Analysis Failed' : 'Analysis Complete' }}</h3>
          <button @click="closeAnalysisModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Error State -->
          <div v-if="analysisError" class="error-state">
            <div class="error-icon">⚠️</div>
            <h4>Analysis Error</h4>
            <p class="error-message">{{ analysisError }}</p>
            <button @click="retryAnalysis" class="btn-retry" :disabled="analysisLoading">
              {{ analysisLoading ? 'Retrying...' : 'Try Again' }}
            </button>
          </div>
          
          <!-- Success State -->
          <div v-else-if="analysisResults">
            <!-- Suggested Name Section -->
            <div v-if="analysisResults?.topic_name" class="suggested-name-section">
              <h4>💡 Suggested Name</h4>
              <div class="suggested-name-box">
                <strong>{{ analysisResults.topic_name }}</strong>
                <button 
                  @click="applySuggestedName" 
                  class="btn-apply-name"
                  :disabled="earworm.name === analysisResults.topic_name"
                >
                  {{ earworm.name === analysisResults.topic_name ? '✅ Applied' : '📝 Use This Name' }}
                </button>
              </div>
            </div>

            <!-- Analysis Details -->
            <details class="analysis-details">
              <summary>📊 View Analysis Details</summary>
              <div class="analysis-preview">
                <pre>{{ JSON.stringify(analysisResults, null, 2) }}</pre>
              </div>
            </details>
          </div>
        </div>
        <div class="modal-footer">
          <button v-if="!analysisError" @click="goBackToEarworms" class="btn-primary">Go Back to Earworms</button>
          <button @click="closeAnalysisModal" class="btn-secondary">
            {{ analysisError ? 'Close' : 'Continue Editing' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Analysis Loading Overlay -->
    <div v-if="analysisLoading" class="loading-overlay">
      <div class="loading-content">
        <div class="spinner"></div>
        <p>Analyzing image with ChatGPT...</p>
      </div>
    </div>
    
    <!-- Analysis Display Modal -->
    <div v-if="showAnalysisDisplay" class="modal-overlay" @click="showAnalysisDisplay = false">
      <div class="modal-content analysis-modal" @click.stop>
        <div class="modal-header">
          <h3>📊 Analysis Results</h3>
          <button @click="showAnalysisDisplay = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="earworm.analysis" class="analysis-display">
            <!-- Topic Name -->
            <div v-if="earworm.analysis.topic_name" class="analysis-section">
              <h4>📝 Topic Name</h4>
              <div class="topic-name">{{ earworm.analysis.topic_name }}</div>
            </div>
            
            <!-- Vocabulary Pairs -->
            <div v-if="earworm.analysis.vocabulary_pairs?.length" class="analysis-section">
              <h4>📚 Vocabulary Pairs ({{ earworm.analysis.vocabulary_pairs.length }})</h4>
              <div class="vocabulary-grid">
                <div 
                  v-for="(pair, index) in earworm.analysis.vocabulary_pairs" 
                  :key="index"
                  class="vocabulary-pair"
                >
                  <div class="english-term">{{ pair.english_term }}</div>
                  <div class="arrow">→</div>
                  <div class="spanish-term">{{ pair.spanish_term }}</div>
                </div>
              </div>
            </div>
            
            <!-- Additional Analysis Data -->
            <div v-if="hasAdditionalAnalysisData" class="analysis-section">
              <h4>🔍 Additional Data</h4>
              <details class="additional-data">
                <summary>View Raw Analysis Data</summary>
                <pre class="analysis-raw">{{ JSON.stringify(earworm.analysis, null, 2) }}</pre>
              </details>
            </div>
            
            <!-- Analysis Metadata -->
            <div v-if="earworm.analysis.analyzed_at" class="analysis-metadata">
              <small class="text-muted">
                Analyzed: {{ formatDate(earworm.analysis.analyzed_at) }}
              </small>
            </div>
          </div>
          
          <div v-else class="no-analysis">
            <p>No analysis data available.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showAnalysisDisplay = false" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>
    
    <!-- Lyrics Display Modal -->
    <div v-if="showLyricsDisplay" class="modal-overlay" @click="showLyricsDisplay = false">
      <div class="modal-content lyrics-modal" @click.stop>
        <div class="modal-header">
          <h3>🎤 Generated Lyrics</h3>
          <button @click="showLyricsDisplay = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="earworm.lyrics" class="lyrics-display">
            <div class="lyrics-content">
              <pre class="lyrics-text">{{ earworm.lyrics }}</pre>
            </div>
            
            <!-- Lyrics Actions -->
            <div class="lyrics-actions">
              <button @click="copyLyricsToClipboard" class="btn-copy-lyrics">
                📋 Copy to Clipboard
              </button>
              <button 
                v-if="earworm.suno_audio_url || earworm.suno_audio_url2"
                @click="playEarworm" 
                class="btn-play-with-lyrics"
              >
                🎵 Play with Lyrics
              </button>
            </div>
          </div>
          
          <div v-else class="no-lyrics">
            <p>No lyrics available.</p>
            <button 
              v-if="uploadedImageUrl" 
              @click="closeLyricsModalAndGenerate" 
              class="btn-generate-lyrics"
            >
              🎵 Generate Lyrics Now
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showLyricsDisplay = false" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>
    
    <!-- Image View Modal -->
    <div v-if="showImageModal" class="modal-overlay image-modal-overlay" @click="showImageModal = false">
      <div class="modal-content image-modal-content" @click.stop>
        <div class="modal-header">
          <h3>📷 Image View</h3>
          <button @click="showImageModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body image-modal-body">
          <div class="image-container-modal">
            <img 
              :src="uploadedImageUrl" 
              alt="Full size preview" 
              class="modal-image"
              @error="imageError = true"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Both Timestamped Lyrics Modal -->
    <div v-if="showBothTimestampedLyrics" class="modal-overlay" @click="showBothTimestampedLyrics = false">
      <div class="modal-content lyrics-modal" @click.stop>
        <div class="modal-header">
          <h3>⏰ Both Timestamped Lyrics</h3>
          <button @click="showBothTimestampedLyrics = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="lyrics-display">
            <div>
              <h4>Track 1</h4>
              <div class="timestamped-lyrics-content">
                <div 
                  v-for="(word, idx) in earworm.suno_ts_lyrics?.alignedWords || []"
                  :key="'track1-' + idx"
                  class="timestamped-word"
                >
                  <span class="word">{{ word.word }}</span>
                  <span class="timestamp">{{ formatTime(word.start) }} - {{ formatTime(word.end) }}</span>
                </div>
              </div>
            </div>
            <div style="margin-top:2rem;">
              <h4>Track 2</h4>
              <div class="timestamped-lyrics-content">
                <div 
                  v-for="(word, idx) in earworm.suno_ts_lyrics2?.alignedWords || []"
                  :key="'track2-' + idx"
                  class="timestamped-word"
                >
                  <span class="word">{{ word.word }}</span>
                  <span class="timestamp">{{ formatTime(word.start) }} - {{ formatTime(word.end) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showBothTimestampedLyrics = false" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>

    <!-- Add: Modal for copying timestamped lyrics -->
    <div v-if="showCopyTimestampedLyricsModal" class="modal-overlay" @click="showCopyTimestampedLyricsModal = false">
      <div class="modal-content lyrics-modal" @click.stop>
        <div class="modal-header">
          <h3>📋 Copy Timestamped Lyrics</h3>
          <button @click="showCopyTimestampedLyricsModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="lyrics-display">
            <div v-if="earworm.suno_ts_lyrics">
              <h4>Track 1</h4>
              <button class="btn-copy-lyrics" @click="copyTrackTimestampedLyrics(1)">Copy Track 1 Timestamped Lyrics</button>
            </div>
            <div v-if="earworm.suno_ts_lyrics2" style="margin-top:1.5rem;">
              <h4>Track 2</h4>
              <button class="btn-copy-lyrics" @click="copyTrackTimestampedLyrics(2)">Copy Track 2 Timestamped Lyrics</button>
            </div>
            <!-- Show a message if neither exists (should not happen if button is visible) -->
            <div v-if="!earworm.suno_ts_lyrics && !earworm.suno_ts_lyrics2" class="no-lyrics">
              No timestamped lyrics available.
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showCopyTimestampedLyricsModal = false" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Header from '../components/Header.vue'
import type { Earworm } from '../services/database'
import type { EarwormStatus } from '../services/database'
import type { EarwormEvent } from '../services/database'

const router = useRouter()
const route = useRoute()

const currentUser = ref<any>(null)
const saving = ref(false)
const imageError = ref(false)
const imageUploading = ref(false)
const uploadedImageUrl = ref('')
const analysisResults = ref<any>(null)
const showAnalysisModal = ref(false)
const analysisLoading = ref(false)
const lyricsLoading = ref(false)
const musicLoading = ref(false)
const analysisError = ref('')
const lastAnalyzedImageUrl = ref('')
const cameraOpen = ref(false)
const videoEl = ref<HTMLVideoElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)

// Add missing modal reactive variables
const showAnalysisDisplay = ref(false)
const showLyricsDisplay = ref(false)
const showImageModal = ref(false)
const showBothTimestampedLyrics = ref(false);
const showCopyTimestampedLyricsModal = ref(false)

const formatTime = (seconds: number): string => {
  if (!seconds && seconds !== 0) return ''
  const mins = Math.floor(seconds / 60)
  const secs = (seconds % 60).toFixed(1)
  return `${mins}:${secs.padStart(4, '0')}`
}

// Task details variables
const loadingTaskDetails = ref(false)
const taskDetails = ref<any>(null)

// Suno status polling
const sunoStatus = ref('')
const sunoStatusMessage = ref('')
const isGeneratingMusic = ref(false)
const sunoAudioUrl = ref('')
let statusPollingInterval: number | null = null
const POLL_INTERVAL_MS = 3000

let mediaStream: MediaStream | null = null

const isEditing = computed(() => !!route.params.id)

// Update the earworm object to match your DB schema
const earworm = reactive<Earworm>({
  id: null as any,
  user_id: null as any,
  name: '',
  source: null,
  image_url: null,
  analysis: null,
  lyrics: null,
  suno_task_id: null,
  suno_task_status: null,
  suno_audio_url: null,
  suno_audio_url2: null,
  suno_audio_id: null,
  suno_audio_id2: null,
  suno_ts_lyrics: null,
  suno_ts_lyrics2: null,
  status: null,
  created_at: null,
  modified_at: null
})

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'

// Computed property to check for additional analysis data
const hasAdditionalAnalysisData = computed(() => {
  if (!earworm.analysis) return false
  const knownKeys = ['topic_name', 'vocabulary_pairs', 'analyzed_at', 'image_url']
  return Object.keys(earworm.analysis).some(key => !knownKeys.includes(key))
})

const parseStatusValue = (rawStatus: any): string | EarwormStatus | null => {
  if (!rawStatus) return null

  if (typeof rawStatus === 'string') {
    try {
      const parsed = JSON.parse(rawStatus)
      if (parsed && typeof parsed === 'object') {
        if (!Array.isArray(parsed.events)) parsed.events = []
        return parsed as EarwormStatus
      }
    } catch {
      return rawStatus
    }
  }

  if (typeof rawStatus === 'object') {
    if (!Array.isArray((rawStatus as EarwormStatus).events)) {
      (rawStatus as EarwormStatus).events = []
    }
    return rawStatus as EarwormStatus
  }

  return rawStatus
}

const deriveStatusFromData = (data: Partial<Earworm>): string => {
  if (data.suno_audio_url || data.suno_audio_url2) return 'tracks complete'
  if (data.suno_task_status && data.suno_task_status.toLowerCase().includes('failed')) return 'generate tracks failed'
  if (data.lyrics) return 'lyrics complete'
  if (data.analysis) return 'analysis complete'
  return 'draft'
}

const getLatestStatusText = (statusValue: string | EarwormStatus | null | undefined): string => {
  if (!statusValue) return ''
  if (typeof statusValue === 'string') return statusValue
  if (statusValue?.lastevent?.text) return statusValue.lastevent.text
  return ''
}

const formatStatusEventTime = (timeValue: string | number) => {
  if (!timeValue && timeValue !== 0) return ''
  const date = typeof timeValue === 'number' ? new Date(timeValue) : new Date(timeValue)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
}

const statusEvents = computed<EarwormEvent[]>(() => {
  const statusValue = earworm.status
  if (statusValue && typeof statusValue === 'object' && Array.isArray((statusValue as EarwormStatus).events)) {
    return [...(statusValue as EarwormStatus).events].sort((a, b) => {
      const timeA = new Date(a.time).getTime()
      const timeB = new Date(b.time).getTime()
      return timeB - timeA
    })
  }
  return []
})

const shouldStopPolling = (statusValue: string | EarwormStatus | null, taskStatus?: string | null) => {
  const latestText = getLatestStatusText(statusValue).toLowerCase()
  const task = (taskStatus || '').toUpperCase()

  if (latestText.includes('failed') || latestText.includes('error')) return true
  if (latestText.includes('complete')) return true

  const terminalTaskStatuses = ['SUCCESS', 'FAILED', 'CREATE_TASK_FAILED', 'GENERATE_AUDIO_FAILED', 'CALLBACK_EXCEPTION', 'SENSITIVE_WORD_ERROR']
  if (terminalTaskStatuses.includes(task)) return true

  return false
}

// Utility functions for modals
const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleString()
  } catch {
    return dateString
  }
}

const copyLyricsToClipboard = async () => {
  if (!earworm.lyrics) return
  
  try {
    await navigator.clipboard.writeText(earworm.lyrics)
    console.log('✅ Lyrics copied to clipboard')
    // Show brief feedback
    const button = document.querySelector('.btn-copy-lyrics') as HTMLElement
    if (button) {
      const originalText = button.textContent
      button.textContent = '✅ Copied!'
      setTimeout(() => {
        button.textContent = originalText
      }, 2000)
    }
  } catch (error) {
    console.error('❌ Failed to copy lyrics:', error)
  }
}

const playEarworm = () => {
  if (earworm.id) {
    console.log('🎵 Playing earworm from builder:', earworm.id)
    router.push(`/earworm-player/${earworm.id}`)
  }
}

const closeLyricsModalAndGenerate = () => {
  showLyricsDisplay.value = false
  handleGenerateLyrics()
}

const openImageInNewTab = () => {
  if (uploadedImageUrl.value) {
    window.open(uploadedImageUrl.value, '_blank')
  }
}

const copyImageUrlToClipboard = async () => {
  if (!uploadedImageUrl.value) return
  
  try {
    await navigator.clipboard.writeText(uploadedImageUrl.value)
    console.log('✅ Image URL copied to clipboard')
    
    // Show brief feedback
    const buttons = document.querySelectorAll('.btn-secondary')
    const copyButton = Array.from(buttons).find(btn => btn.textContent?.includes('Copy URL')) as HTMLElement
    if (copyButton) {
      const originalText = copyButton.textContent
      copyButton.textContent = '✅ Copied!'
      setTimeout(() => {
        copyButton.textContent = originalText
      }, 2000)
    }
  } catch (error) {
    console.error('❌ Failed to copy image URL:', error)
  }
}

// Complete the uploadImageToCloudinary function
const uploadImageToCloudinary = async (dataUrl: string) => {
  imageUploading.value = true
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }

    // If we don't have an earworm ID yet, create the earworm first
    if (!earworm.id) {
      console.log('Creating earworm record before image upload...')
      await createEarwormRecord()
    }
    
    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ image: dataUrl })
    })
    
    if (!response.ok) {
      if (response.status === 413) {
        throw new Error('Image file is too large. Please try a smaller image or compress it further.')
      }
      throw new Error(`Upload failed with status ${response.status}`)
    }
    
    const result = await response.json()
    uploadedImageUrl.value = result.url
    console.log('Image uploaded successfully:', result.url)
    
    // Update the earworm record with the image URL
    if (earworm.id) {
      await updateEarwormImage(earworm.id, result.url)
    }
    
    console.log('✅ Image uploaded and saved to earworm record')
    
  } catch (error) {
    console.error('Image upload failed:', error)
    alert('Failed to upload image: ' + (error instanceof Error ? error.message : 'Unknown error'))
  } finally {
    imageUploading.value = false
  }
}

// Add missing functions that are referenced in the template
const deletePhoto = async () => {
  if (!confirm('Are you sure you want to delete this photo? This will also clear the analysis results.')) {
    return
  }
  
  try {
    console.log('🗑️ Deleting photo and clearing analysis')
    
    // Clear the uploaded image
    uploadedImageUrl.value = ''
    imageError.value = false
    
    // Clear analysis results
    analysisResults.value = null
    analysisError.value = ''
    showAnalysisModal.value = false
    lastAnalyzedImageUrl.value = ''
    
    // Clear earworm analysis data
    earworm.analysis = null
    
    // Reset name to default if it was likely auto-generated from analysis
    if (analysisResults.value?.topic_name && earworm.name === analysisResults.value.topic_name) {
      earworm.name = 'Untitled Earworm'
      console.log('🏷️ Reset name to default since photo was deleted')
    }
    
    // If editing an existing earworm, update the database to remove the image
    if (earworm.id) {
      await updateEarwormImage(earworm.id, null)
      console.log('✅ Removed image from database')
    }
    
    console.log('✅ Photo deleted successfully')
    
  } catch (error) {
    console.error('❌ Error deleting photo:', error)
    alert('Failed to delete photo')
  }
}

const analyzeImageWithChatGPT = async (imageUrl: string) => {
  // Prevent duplicate requests for the same image
  if (analysisLoading.value || lastAnalyzedImageUrl.value === imageUrl) {
    console.log('🚫 Skipping duplicate analysis request for:', imageUrl)
    return
  }
  
  analysisLoading.value = true
  analysisError.value = ''
  lastAnalyzedImageUrl.value = imageUrl
  
  try {
    console.log('🔍 Starting ChatGPT analysis for image:', imageUrl)
    
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const requestBody = {
      use_default_prompt: true,
      image_url: imageUrl,
      earworm_id: earworm.id
    }
    
    const response = await fetch(`${API_BASE_URL}/api/chatgpt/analyze-doc`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${data.details || 'Unknown error'}`)
    }
    
    console.log('✅ ChatGPT analysis completed successfully')
    analysisResults.value = data.response
    
    // Update earworm analysis
    earworm.analysis = data.response
    
  } catch (error) {
    console.error('❌ ChatGPT analysis failed:', error)
    
    let errorMessage = 'Failed to analyze image with ChatGPT'
    if (error instanceof Error) {
      if (error.message.includes('Rate limit') || error.message.includes('429')) {
        errorMessage = 'Rate limit exceeded. Please wait a few minutes and try again.'
      } else {
        errorMessage = error.message
      }
    }
    
    analysisError.value = errorMessage
    showAnalysisModal.value = true
    
  } finally {
    analysisLoading.value = false
  }
}

const retryAnalysis = async () => {
  if (uploadedImageUrl.value) {
    lastAnalyzedImageUrl.value = ''
    await analyzeImageWithChatGPT(uploadedImageUrl.value)
  }
}

const closeAnalysisModal = () => {
  showAnalysisModal.value = false
  analysisError.value = ''
}

const applySuggestedName = () => {
  if (analysisResults.value?.topic_name) {
    earworm.name = analysisResults.value.topic_name
    console.log('✅ Applied suggested name:', earworm.name)
  }
}

const goBackToEarworms = () => {
  router.push({ name: 'earworms' })
}

const handleGenerateLyrics = async () => {
  try {
    lyricsLoading.value = true
    
    if (!earworm.id) {
      console.warn('Earworm not saved yet. Please save the earworm first.')
      return
    }
    
    console.log('🎵 Starting lyrics generation for earworm:', earworm.id)
    
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const response = await fetch(`${API_BASE_URL}/api/chatgpt/generate-lyrics`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        earworm_id: earworm.id
      })
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to generate lyrics`)
    }
    
    const data = await response.json()
    console.log('✅ Lyrics generated successfully')
    
    // Update earworm lyrics
    earworm.lyrics = data.lyrics_data.lyrics
    
  } catch (error) {
    console.error('❌ Error generating lyrics:', error)
    alert('Failed to generate lyrics: ' + (error instanceof Error ? error.message : 'Unknown error'))
  } finally {
    lyricsLoading.value = false
  }
}

const handleGenerateMusic = async () => {
  try {
    musicLoading.value = true
    
    if (!earworm.id) {
      console.warn('Earworm not saved yet. Please save the earworm first.')
      return
    }
    
    console.log('🎶 Starting music generation for earworm:', earworm.id)
    
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}/generate-music`, {
      method: 'POST',
      headers
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to generate music`)
    }
    
    const musicData = await response.json()
    console.log('✅ Music generation started successfully:', musicData)
    
    if (musicData.taskId) {
      earworm.suno_task_id = musicData.taskId
      earworm.suno_task_status = 'pending'
      startStatusPolling()
    }
    
  } catch (error) {
    console.error('❌ Error generating music:', error)
    alert('Failed to generate music: ' + (error instanceof Error ? error.message : 'Unknown error'))
  } finally {
    musicLoading.value = false
  }
}

// Add missing status polling functions
const startStatusPolling = () => {
  if (statusPollingInterval) {
    clearInterval(statusPollingInterval)
  }

  isGeneratingMusic.value = true
  console.log('🔄 Starting status polling...')

  pollEarwormStatus()
  statusPollingInterval = window.setInterval(pollEarwormStatus, POLL_INTERVAL_MS)
}

const stopStatusPolling = () => {
  if (statusPollingInterval) {
    clearInterval(statusPollingInterval)
    statusPollingInterval = null
    console.log('⏹️ Stopped status polling')
  }
  isGeneratingMusic.value = false
}

const pollEarwormStatus = async () => {
  if (!earworm.id) return

  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {}
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}/status`, { headers })
    
    if (!response.ok) {
      console.error('Failed to poll earworm status:', response.status)
      return
    }
    
    const data = await response.json()
    if (!data?.success || !data.earworm) {
      console.warn('Earworm status payload missing data')
      return
    }

    const earwormData = data.earworm as Partial<Earworm>
    const parsedStatus = parseStatusValue(earwormData.status)
    earworm.status = parsedStatus ?? deriveStatusFromData(earwormData)

    earworm.suno_task_status = earwormData.suno_task_status ?? earworm.suno_task_status
    earworm.suno_task_id = earwormData.suno_task_id ?? earworm.suno_task_id
    earworm.suno_audio_url = earwormData.suno_audio_url ?? earworm.suno_audio_url
    earworm.suno_audio_url2 = earwormData.suno_audio_url2 ?? earworm.suno_audio_url2
    earworm.suno_audio_id = earwormData.suno_audio_id ?? earworm.suno_audio_id
    earworm.suno_audio_id2 = earwormData.suno_audio_id2 ?? earworm.suno_audio_id2
    earworm.lyrics = earwormData.lyrics ?? earworm.lyrics
    earworm.analysis = earwormData.analysis ?? earworm.analysis
    earworm.suno_ts_lyrics = earwormData.suno_ts_lyrics ?? earworm.suno_ts_lyrics
    earworm.suno_ts_lyrics2 = earwormData.suno_ts_lyrics2 ?? earworm.suno_ts_lyrics2

    sunoStatus.value = earworm.suno_task_status || ''
    sunoStatusMessage.value = earwormData.status_message || ''
    sunoAudioUrl.value = earworm.suno_audio_url || ''

    if (shouldStopPolling(earworm.status, earworm.suno_task_status)) {
      stopStatusPolling()
    }
  } catch (error) {
    console.error('Error polling earworm status:', error)
  }
}

const checkInitialStatus = async () => {
  if (earworm.id && earworm.suno_task_id && 
      (earworm.suno_task_status === 'pending' || earworm.suno_task_status === 'processing' || earworm.suno_task_status === 'generating')) {
    console.log('🔍 Found pending Suno task, starting status polling')
    startStatusPolling()
  }
}

const createEarwormRecord = async () => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const payload = {
      name: earworm.name,
      user_id: currentUser.value?.id || null,
      source: earworm.source,
      image_url: uploadedImageUrl.value || null,
      analysis: earworm.analysis,
      lyrics: earworm.lyrics,
      suno_task_id: earworm.suno_task_id,
      suno_task_status: earworm.suno_task_status,
      suno_audio_url: earworm.suno_audio_url,
      suno_audio_url2: earworm.suno_audio_url2,
      suno_audio_id: earworm.suno_audio_id,
      suno_audio_id2: earworm.suno_audio_id2,
      status: earworm.status
      // ...add/remove fields as needed per your schema...
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })
    
    if (!response.ok) {
      throw new Error('Failed to create earworm')
    }
    
    const result = await response.json()
    earworm.id = result.id
    console.log('Created earworm record with ID:', result.id)
    
    return result
  } catch (error) {
    console.error('Failed to create earworm record:', error)
    throw error
  }
}

const updateEarwormImage = async (earwormId: string | number, imageUrl: string | null) => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const idString = String(earwormId)
    
    const fetchResponse = await fetch(`${API_BASE_URL}/api/earworms/${idString}`, { headers })
    if (!fetchResponse.ok) {
      throw new Error('Failed to fetch earworm data')
    }
    
    const currentData = await fetchResponse.json()
    
    const updatePayload = {
      name: currentData.name,
      user_id: currentData.user_id,
      source: currentData.source,
      image_url: imageUrl,
      analysis: currentData.analysis,
      lyrics: currentData.lyrics,
      suno_task_id: currentData.suno_task_id,
      suno_task_status: currentData.suno_task_status,
      suno_audio_url: currentData.suno_audio_url,
      suno_audio_url2: currentData.suno_audio_url2,
      suno_audio_id: currentData.suno_audio_id,
      suno_audio_id2: currentData.suno_audio_id2,
      status: currentData.status
      // ...add/remove fields as needed per your schema...
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${idString}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updatePayload)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error('Failed to update earworm with image: ' + errorText)
    }
    
    const result = await response.json()
    console.log('Earworm updated successfully:', result)
  } catch (error) {
    console.error('Failed to update earworm with image:', error)
  }
}

const saveAudioData = async (earwormId: string | number, audioData: {
  firstAudioUrl?: string,
  firstAudioId?: string,
  secondAudioUrl?: string | null,
  secondAudioId?: string | null
}) => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const idString = String(earwormId)
    
    const fetchResponse = await fetch(`${API_BASE_URL}/api/earworms/${idString}`, { headers })
    if (!fetchResponse.ok) {
      throw new Error('Failed to fetch earworm data')
    }
    
    const currentData = await fetchResponse.json()
    
    const updatePayload = {
      name: currentData.name,
      user_id: currentData.user_id,
      source: currentData.source,
      image_url: currentData.image_url,
      analysis: currentData.analysis,
      lyrics: currentData.lyrics,
      suno_task_id: currentData.suno_task_id,
      suno_task_status: currentData.suno_task_status,
      suno_audio_url: audioData.firstAudioUrl || currentData.suno_audio_url,
      suno_audio_url2: audioData.secondAudioUrl || currentData.suno_audio_url2,
      suno_audio_id: audioData.firstAudioId || currentData.suno_audio_id,
      suno_audio_id2: audioData.secondAudioId || currentData.suno_audio_id2,
      status: currentData.status
      // ...add/remove fields as needed per your schema...
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${idString}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updatePayload)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error('Failed to update earworm with audio data: ' + errorText)
    }
    
    if (audioData.firstAudioUrl) earworm.suno_audio_url = audioData.firstAudioUrl
    if (audioData.secondAudioUrl) earworm.suno_audio_url2 = audioData.secondAudioUrl
    if (audioData.firstAudioId) earworm.suno_audio_id = audioData.firstAudioId
    if (audioData.secondAudioId) earworm.suno_audio_id2 = audioData.secondAudioId
    
  } catch (error) {
    console.error('❌ Failed to save audio data:', error)
    alert('Failed to save audio data to database.')
  }
}

// Add missing utility functions for task details
const copyToClipboard = async (text: string, inputId: string) => {
  try {
    await navigator.clipboard.writeText(text)
    console.log('✅ Copied to clipboard:', text)
    const input = document.getElementById(inputId) as HTMLInputElement
    if (input) {
      input.select()
      input.style.background = '#d4edda'
      setTimeout(() => {
        input.style.background = '#f8f9fa'
      }, 1000)
    }
  } catch (error) {
    console.error('❌ Failed to copy to clipboard:', error)
    const input = document.getElementById(inputId) as HTMLInputElement
    if (input) {
      input.select()
    }
  }
}

// Add missing function for copying timestamped lyrics
const copyTrackTimestampedLyrics = async (trackNumber: number) => {
  let lyricsData: any = null
  if (trackNumber === 1) {
    lyricsData = earworm.suno_ts_lyrics
  } else if (trackNumber === 2) {
    lyricsData = earworm.suno_ts_lyrics2
  }
  if (!lyricsData || !lyricsData.alignedWords) {
    alert('No timestamped lyrics found for this track.')
    return
  }
  // Format as: word [start-end]
  const formatted = lyricsData.alignedWords
    .map((w: any) => `${w.word} [${formatTime(w.start)}-${formatTime(w.end)}]`)
    .join(' ')
  try {
    await navigator.clipboard.writeText(formatted)
    alert(`Copied timestamped lyrics for Track ${trackNumber} to clipboard!`)
  } catch (e) {
    alert('Failed to copy timestamped lyrics.')
  }
}

const playAudio = (audioUrl: string) => {
  const audio = new Audio(audioUrl)
  audio.play().catch(error => {
    console.error('❌ Failed to play audio:', error)
  })
}

const openInNewTab = (url: string) => {
  window.open(url, '_blank')
}

// Add missing camera functions
const openCamera = async () => {
  try {
    cameraOpen.value = true
    mediaStream = await navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 1655 }
      } 
    })
    if (videoEl.value) {
      videoEl.value.srcObject = mediaStream
    }
  } catch (err) {
    console.error('Camera access error:', err)
    alert('Unable to access camera')
    cameraOpen.value = false
  }
}

const closeCamera = () => {
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop())
    mediaStream = null
  }
  if (videoEl.value) {
    videoEl.value.srcObject = null
  }
  cameraOpen.value = false
}

const capturePhoto = () => {
  const video = videoEl.value
  if (!video) return
  
  const canvas = document.createElement('canvas')
  const srcWidth = video.videoWidth || 640
  const srcHeight = video.videoHeight || 480
  
  const documentAspectRatio = 8.5 / 11
  let cropWidth = srcWidth
  let cropHeight = srcHeight
  let cropX = 0
  let cropY = 0
  
  const currentAspectRatio = srcWidth / srcHeight
  
  if (currentAspectRatio > documentAspectRatio) {
    cropWidth = srcHeight * documentAspectRatio
    cropX = (srcWidth - cropWidth) / 2
  } else {
    cropHeight = srcWidth / documentAspectRatio
    cropY = (srcHeight - cropHeight) / 2
  }
  
  const maxWidth = 640
  const targetWidth = maxWidth
  const targetHeight = maxWidth / documentAspectRatio
  
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.drawImage(video, cropX, cropY, cropWidth, cropHeight, 0, 0, targetWidth, targetHeight)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
    uploadImageToCloudinary(dataUrl)
  }
  
  closeCamera()
}

const openGallery = () => {
  if (fileInput.value) {
    fileInput.value.removeAttribute('capture')
    fileInput.value.setAttribute('accept', 'image/*')
    fileInput.value.click()
  }
}

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) {
    compressAndUploadImage(file)
  }
}

const compressAndUploadImage = (file: File) => {
  const reader = new FileReader()
  reader.onload = (e) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      
      const maxWidth = 1200
      let { width, height } = img
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }
      
      canvas.width = width
      canvas.height = height
      ctx.drawImage(img, 0, 0, width, height)
      const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8)
      
      uploadImageToCloudinary(compressedDataUrl)
    }
    img.src = e.target?.result as string
  }
  reader.readAsDataURL(file)
}

// Add missing task details functions
const getTaskDetails = async () => {
  if (!earworm.suno_task_id) {
    alert('No task ID found')
    return
  }
  
  loadingTaskDetails.value = true
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/suno/record-info?taskId=${earworm.suno_task_id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      }
    )
    
    if (!response.ok) {
      throw new Error(`Failed to get task details: ${response.status}`)
    }
    
    const data = await response.json()
    taskDetails.value = data
    
    const songs = data.response?.data?.response?.sunoData
    const songCount = songs?.length || 0
    
    if (songCount > 0) {
      const firstAudioUrl = songs[0]?.audioUrl
      const firstAudioId = songs[0]?.id
      const secondAudioUrl = songCount > 1 ? songs[1]?.audioUrl : null
      const secondAudioId = songCount > 1 ? songs[1]?.id : null
      
      if (earworm.id) {
        await saveAudioData(earworm.id, {
          firstAudioUrl,
          firstAudioId,
          secondAudioUrl,
          secondAudioId
        })
      }
    }
    
  } catch (error) {
    console.error('❌ Error getting task details:', error)
    alert('Failed to get task details: ' + (error as Error).message)
  } finally {
    loadingTaskDetails.value = false
  }
}

// Lifecycle hooks
onMounted(async () => {
  const userStr = sessionStorage.getItem('currentUser')
  if (userStr) {
    currentUser.value = JSON.parse(userStr)
  }
    
  if (isEditing.value) {
    const earwormId = route.params.id as string
    console.log('📝 Editing mode - earworm ID from route:', earwormId, typeof earwormId)
    
    // Validate the ID before proceeding
    if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
      console.error('❌ Invalid earworm ID in route params:', earwormId)
      router.push({ name: 'earworms' })
      return
    }
    
    // Set the earworm ID as string (server expects string UUIDs)
    earworm.id = earwormId
    await fetchEarwormDetails(earwormId)
  }
  
  // Debug logging
  console.log('[EarwormBuilder] onMounted: earworm.suno_ts_lyrics:', earworm.suno_ts_lyrics)
  console.log('[EarwormBuilder] onMounted: earworm.suno_ts_lyrics2:', earworm.suno_ts_lyrics2)
  showImageModal.value = false // Ensure modal is closed on mount
})

// Remove unnecessary type checking for status and EarwormStatus/EarwormEvent
const getStepStatus = (step: string) => {
  let statusText: string | undefined

  if (earworm.status && typeof (earworm.status as any).lastevent === 'object' && (earworm.status as any).lastevent.text) {
    statusText = (earworm.status as any).lastevent.text
  } else {
    statusText = earworm.status as any
  }

  if (!statusText || statusText === 'draft') {
    return 'pending'
  }

  const stepOrder = ['analyzing worksheet', 'writing lyrics', 'generating tracks']
  const currentStepIndex = stepOrder.findIndex(s => statusText!.includes(s))
  const targetStepIndex = stepOrder.findIndex(s => step === s)

  if (statusText.includes('failed')) {
    if (targetStepIndex <= currentStepIndex) {
      return 'failed'
    }
    return 'pending'
  }

  if (statusText === 'tracks complete') {
    return 'completed'
  }

  if (targetStepIndex < currentStepIndex) {
    return 'completed'
  } else if (targetStepIndex === currentStepIndex) {
    return 'active'
  } else {
    return 'pending'
  }
}


// When fetching earworm details, ensure status is parsed as object if possible
const fetchEarwormDetails = async (earwormId: string) => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }

    // Validate earwormId
    if (!earwormId || earwormId === 'undefined' || earwormId === 'null') {
      throw new Error('Invalid earworm ID provided')
    }

    console.log('🔍 Fetching earworm details for ID:', earwormId)

    const response = await fetch(`${API_BASE_URL}/api/earworms/${earwormId}`, { headers })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Earworm not found')
      }
      throw new Error(`Server error: ${response.status}`)
    }

    const data = await response.json()
    console.log('📡 Received earworm data:', data)

    // Update earworm object with received data
    earworm.name = data.name || 'Untitled Earworm'
    earworm.user_id = data.user_id || null
    earworm.source = data.source || null
    earworm.image_url = data.image_url || null
    uploadedImageUrl.value = data.image_url || ''
    earworm.analysis = data.analysis || null
    earworm.lyrics = data.lyrics || null
    earworm.suno_task_id = data.suno_task_id || ''
    earworm.suno_task_status = data.suno_task_status || 'pending'
    earworm.suno_audio_url = data.suno_audio_url || ''
    earworm.suno_audio_url2 = data.suno_audio_url2 || ''
    earworm.suno_audio_id = data.suno_audio_id || ''
    earworm.suno_audio_id2 = data.suno_audio_id2 || ''
    earworm.suno_ts_lyrics = data.suno_ts_lyrics || null
    earworm.suno_ts_lyrics2 = data.suno_ts_lyrics2 || null
    earworm.created_at = data.created_at || null
    earworm.modified_at = data.modified_at || null

    // Determine status based on what data is available
    let derivedStatus = data.status || 'draft'
    if (!data.status) {
      if (data.suno_audio_url || data.suno_audio_url2) {
        derivedStatus = 'tracks complete'
      } else if (data.lyrics) {
        derivedStatus = 'lyrics complete'
      } else if (data.analysis) {
        derivedStatus = 'analysis complete'
      } else {
        derivedStatus = 'draft'
      }
      console.log('🔍 Derived status from data:', derivedStatus)
    }

    // Parse status as object if possible
    let parsedStatus = data.status
    if (typeof parsedStatus === 'string') {
      try {
        parsedStatus = JSON.parse(parsedStatus)
      } catch {
        // fallback to string
      }
    }
    earworm.status = parsedStatus || derivedStatus

    // Debug logging
    console.log('[EarwormBuilder] fetchEarwormDetails: suno_ts_lyrics:', data.suno_ts_lyrics)
    console.log('[EarwormBuilder] fetchEarwormDetails: suno_ts_lyrics2:', data.suno_ts_lyrics2)
    console.log('[EarwormBuilder] after assignment: earworm.suno_ts_lyrics:', earworm.suno_ts_lyrics)
    console.log('[EarwormBuilder] after assignment: earworm.suno_ts_lyrics2:', earworm.suno_ts_lyrics2)
    console.log('✅ Earworm data loaded successfully with status:', earworm.status)

    // Start polling if earworm is in progress
    if (
      earworm.status &&
      (
        (typeof earworm.status === 'string' && earworm.status !== 'draft' && earworm.status !== 'tracks complete' && !earworm.status.includes('failed')) ||
        (typeof (earworm.status as any).lastevent === 'object' && (earworm.status as any).lastevent.text && !['draft', 'tracks complete'].includes((earworm.status as any).lastevent.text) && !(earworm.status as any).lastevent.text.includes('failed'))
      )
    ) {
      startStatusPolling()
    }
  } catch (error) {
    console.error('❌ Failed to load earworm:', error)
    alert(`Failed to load earworm: ${error instanceof Error ? error.message : 'Unknown error'}`)
    router.push({ name: 'earworms' })
  }
}

// Add the missing deleteEarworm function for Delete button
const deleteEarworm = async () => {
  if (!earworm.id) return
  if (!confirm('Are you sure you want to delete this earworm? This cannot be undone.')) {
    return
  }
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}`, {
      method: 'DELETE',
      headers
    })
    if (!response.ok) {
      throw new Error('Failed to delete earworm')
    }
    router.push({ name: 'earworms' })
  } catch (error) {
    console.error('Failed to delete earworm:', error)
    alert('Failed to delete earworm')
  }
}

// Remove duplicate getFailureMessage if present
// ...existing code...

const goBack = () => {
  router.push({ name: 'earworms' })
}


// Add the missing buildEarworm function
const building = ref(false)

const buildEarworm = async () => {
  if (!uploadedImageUrl.value) {
    alert('Please upload a worksheet image first.')
    return
  }
  
  building.value = true
  
  try {
    console.log('🏗️ Starting earworm build process')
    
    // Ensure we have an earworm record
    if (!earworm.id) {
      await createEarwormRecord()
    }
    
    // Set initial status
    earworm.status = 'analyzing worksheet'
    
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }
    
    const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}/build`, {
      method: 'POST',
      headers
    })
    
    if (!response.ok) { 
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to start build process')
    }
    
    console.log('✅ Build process started successfully')
    
    // Start polling for status updates
    startStatusPolling()
    
  } catch (error) {
    console.error('❌ Error building earworm:', error)
    alert('Failed to build earworm: ' + (error instanceof Error ? error.message : 'Unknown error'))
    earworm.status = 'draft'
  } finally {
    building.value = false
  }
}

// Add this function to create a new earworm and redirect to builder with the new id
const createAndGoToNewEarworm = async () => {
  try {
    const userStr = sessionStorage.getItem('currentUser')
    const userData = userStr ? JSON.parse(userStr) : null
    const sessionToken = userData?.sessionToken

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`
    }

    // Create a default earworm record
    const payload = {
      name: 'Untitled Earworm',
      user_id: userData?.id || null,
      source: null,
      image_url: null,
      analysis: null,
      lyrics: null,
      suno_task_id: null,
      suno_task_status: null,
      suno_audio_url: null,
      suno_audio_url2: null,
      suno_audio_id: null,
      suno_audio_id2: null,
    }

    const response = await fetch(`${API_BASE_URL}/api/earworms`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      throw new Error('Failed to create new earworm')
    }

    const result = await response.json()
    // Redirect to builder with new id
    router.push({ name: 'earworm-builder', params: { id: result.id } })
  } catch (error) {
    alert('Failed to create new earworm: ' + (error instanceof Error ? error.message : 'Unknown error'))
  }
}

// Add the missing saveEarworm function for form submission
const saveEarworm = async () => {
  saving.value = true
  try {
    // If creating new, call createEarwormRecord, else update existing
    if (!earworm.id) {
      await createEarwormRecord()
      alert('Earworm created successfully!')
    } else {
      // Update existing earworm
      const userStr = sessionStorage.getItem('currentUser')
      const userData = userStr ? JSON.parse(userStr) : null
      const sessionToken = userData?.sessionToken
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`
      }
      const payload = {
        name: earworm.name,
        user_id: earworm.user_id,
        source: earworm.source,
        image_url: uploadedImageUrl.value || earworm.image_url,
        analysis: earworm.analysis,
        lyrics: earworm.lyrics,
        suno_task_id: earworm.suno_task_id,
        suno_task_status: earworm.suno_task_status,
        suno_audio_url: earworm.suno_audio_url,
        suno_audio_url2: earworm.suno_audio_url2,
        suno_audio_id: earworm.suno_audio_id,
        suno_audio_id2: earworm.suno_audio_id2,
        status: earworm.status
      }
      const response = await fetch(`${API_BASE_URL}/api/earworms/${earworm.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      })
      if (!response.ok) {
        throw new Error('Failed to update earworm')
      }
      alert('Earworm saved successfully!')
    }
    router.push({ name: 'earworms' })
  } catch (error) {
    alert('Failed to save earworm: ' + (error instanceof Error ? error.message : 'Unknown error'))
  } finally {
    saving.value = false
  }
}

onUnmounted(() => {
  stopStatusPolling()
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop())
  }
})
</script>

<style scoped>
.earworm-builder-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.page-main {
  padding: 2rem 0;
}

.form-section {
  margin-bottom: 2.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-help {
  color: #6c757d;
  font-size: 0.9rem;
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
   font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.camera-btn {
  background: #007bff;
  color: white;
}

.camera-btn:hover {
  background: #0056b3;
}

.gallery-btn {
  background: #28a745;
  color: white;
}

.gallery-btn:hover {
  background: #218838;
}

.image-upload-section {
  display: flex;
  gap: 1rem;
}

.image-preview {
  margin-top: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}



.preview-image {
  width: 100%;
  height: auto;
  display: block;
}

.delete-photo-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0.5rem;
  transition: background-color 0.2s;
}

.delete-photo-btn:hover {
  background: rgba(255, 255, 255, 0.6);
}

.image-overlay-hint {
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.audio-players {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.audio-player-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner-small {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 800px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0,   0,  0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  color: #333;
  background: rgba(0, 0, 0, 0.1);
}

.modal-body {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
  justify-content: flex-end;
   flex-shrink: 0;
}

.analysis-display,
.lyrics-display {
  height: 100%;
  overflow-y: auto;
}

.analysis-section {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.analysis-section:last-child {
   border-bottom: none;
  margin-bottom: 0;
}

.analysis-section h4 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.3rem;
  font-weight: 600;
}

.topic-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2196f3;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
  margin-bottom: 1rem;
}

.vocabulary-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.vocabulary-pair {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.english-term {
  flex: 1;
  font-weight: 500;
  color: #495057;
  font-size: 1.1rem;
}

.arrow {
  color: #6c757d;
  font-weight: bold;
  font-size: 1.2rem;
}

.spanish-term {
  flex: 1;
  font-weight: 600;
  color: #28a745;
  font-size: 1.1rem;
}

.additional-data {
  margin-top: 1rem;
}

.additional-data summary {
  cursor: pointer;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  font-weight: 500;
  font-size: 1.1rem;
}

.additional-data summary:hover {
  background: #e9ecef;
}

.analysis-raw {
   background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 1.5rem;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  line-height: 1.4;
  white-space: pre-wrap;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}

.analysis-metadata {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
  text-align: center;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
  font-size: 1rem;
}

.no-analysis,
.no-lyrics {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  font-size: 1.2rem;
}

.lyrics-content {
  margin-bottom: 2rem;
}

.lyrics-text {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 2rem;
  font-family: inherit;
  font-size: 1.1rem;
  line-height: 1.8;
  white-space: pre-wrap;
  margin: 0;
  color: #333;
  min-height: 300px;
}

.lyrics-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.btn-copy-lyrics {
  background: #17a2b8;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn-copy-lyrics:hover {
  background: #138496;
}

.btn-play-with-lyrics {
  background: #28a745;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn-play-with-lyrics:hover {
  background: #218838;
}

.btn-generate-lyrics {
  background: #28a745;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn-generate-lyrics:hover {
  background: #218838;
}

/* Build Process Styles */
.build-section {
  text-align: center;
  padding: 2rem;
  border: 2px dashed #e9ecef;
  border-radius: 12px;
  background: #f8f9fa;
}

.build-description {
  font-size: 1.1rem;
  color: #495057;
  margin-bottom: 1rem;
}

.build-steps-list {
  text-align: left;
  max-width: 400px;
  margin: 1.5rem auto;
  padding-left: 0;
  list-style: none;
}

.build-steps-list li {
  padding: 0.5rem 0;
  font-size: 1rem;
  color: #495057;
}

.btn-build-earworm {
  background: linear-gradient(135deg, #43a047, #66bb6a);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(67, 160, 71, 0.3);
}

.btn-build-earworm:hover:not(:disabled) {
  background: linear-gradient(135deg, #388e3c, #43a047);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(67, 160, 71, 0.4);
}

.btn-build-earworm:disabled {
  background: #adb5bd;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Build Status Styles */
.build-status {
  padding: 1.5rem;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: white;
}

.status-steps {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  position: relative;
}

.status-steps::before {
  content: '';
  position: absolute;
  top: 30px;
  left: 60px;
  right: 60px;
  height: 2px;
  background: #e9ecef;
  z-index: 1;
}

.status-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  flex: 1;
}

.step-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  position: relative;
  background: #f8f9fa;
  border: 3px solid #e9ecef;
  transition: all 0.3s ease;
}

.step-text {
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  color: #6c757d;
}

.step-spinner {
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  border: 3px solid transparent;
  border-top: 3px solid #43a047;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.status-step.pending .step-icon {
  background: #f8f9fa;
  border-color: #e9ecef;
}

.status-step.active .step-icon {
  background: #e3f2fd;
  border-color: #43a047;
  color: #43a047;
}

.status-step.active .step-text {
  color: #43a047;
  font-weight: 600;
}

.status-step.completed .step-icon {
  background: #e8f5e8;
  border-color: #43a047;
  color: #43a047;
}

.status-step.completed .step-text {
  color: #43a047;
}

.status-step.failed .step-icon {
  background: #ffebee;
  border-color: #f44336;
  color: #f44336;
}

.status-step.failed .step-text {
  color: #f44336;
}

.status-message {
  text-align: center;
  margin-bottom: 1.5rem;
}

.status-text {
  font-size: 1.1rem;
  padding: 1rem;
  border-radius: 8px;
  background: #f8f9fa;
  color: #495057;
}

.status-text.success {
  background: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.status-text.error {
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
}

.build-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-play-earworm {
  background: linear-gradient(135deg, #673ab7, #9c27b0);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.btn-play-earworm:hover {
  background: linear-gradient(135deg, #5e35b1, #8e24aa);
  transform: translateY(-1px);
}

.delete-btn {
  background: #dc3545;
  color: white;
}

.delete-btn:hover {
  background: #c82333;
}

/* Responsive design for full screen modals */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0;
  }
  
  .modal-content {
    border-radius: 0;
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-header h3 {
    font-size: 1.3rem;
  }
  
  .close-btn {
    width: 36px;
    height: 36px;
    font-size: 1.5rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .modal-footer button {
    width: 100%;
  }
  
  .vocabulary-pair {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    text-align: center;
  }
  
  .arrow {
    align-self: center;
    transform: rotate(90deg);
  }
  
  .lyrics-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .btn-copy-lyrics,
  .btn-play-with-lyrics,
  .btn-generate-lyrics {
    width: 100%;
    max-width: 300px;
  }
  
  .topic-name {
    font-size: 1.3rem;
    padding: 1rem;
  }
  
  .english-term,
  .spanish-term {
    font-size: 1rem;
  }
  
  .lyrics-text {
    padding: 1rem;
    font-size: 1rem;
    line-height: 1.6;
  }
}

/* Custom scrollbar for modal content */
.modal-body::-webkit-scrollbar {
  width:  8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Add missing button styles */
.btn-view-analysis {
  background: #9c27b0;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-view-analysis:hover {
  background: #7b1fa2;
}

.btn-view-lyrics {
  background: #ff9800;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color  0.2s;
}

.btn-view-lyrics:hover {
  background: #f57c00;
}

.btn-analyze {
  background: #17a2b8;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-analyze:hover:not(:disabled) {
  background: #138496;
}

.btn-analyze:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.btn-generate-lyrics {
  background: #28a745;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-generate-lyrics:hover:not(:disabled) {
  background: #218838;
}

.btn-generate-lyrics:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.btn-generate-music {
  background: #6f42c1;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-generate-music:hover:not(:disabled) {
  background: #5a2d91;
}

.btn-generate-music:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.task-details-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.task-details-btn:hover:not(:disabled) {
  background: #0056b3;
}

.task-details-btn:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.btn-delete-photo {
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-delete-photo:hover {
  background: #c82333;
}

.btn-primary {
  background: #43a047;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #388e3c;
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-danger {
  background: #f44336;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-danger:hover {
  background: #d32f2f;
}

/* Add missing camera styles */
.camera-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.camera-modal {
  background: black;
  border-radius: 12px;
  padding: 1rem;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.camera-preview-container {
  position: relative;
  width: 100%;
  max-width: 400px;
}

.camera-video {
  width: 100%;
  aspect-ratio: 0.773;
  border-radius: 8px;
  object-fit: cover;
}

.document-frame {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-corners {
  position: absolute;
  inset: 10%;
  border: 2px solid rgba(67, 160, 71, 0.8);
  border-radius: 4px;
}

.corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid #43a047;
}

.corner.top-left {
  top: -3px;
  left: -3px;
  border-right: none;
  border-bottom: none;
}

.corner.top-right {
  top: -3px;
  right: -3px;
  border-left: none;
  border-bottom: none;
}

.corner.bottom-left {
  bottom: -3px;
  left: -3px;
  border-right: none;
  border-top: none;
}

.corner.bottom-right {
  bottom: -3px;
  right: -3px;
  border-left: none;
  border-top: none;
}

.document-guide-text {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.9rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.7);
  padding: 0.5rem 1rem;
  border-radius: 4px;
}

.camera-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.action-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.capture-btn {
  background: #43a047;
  color: white;
}

.capture-btn:hover {
  background: #388e3c;
}

.cancel-btn {
  background: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background: #5a6268;
}

/* Add missing form styles */
.earworm-form {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #eee;
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section h3 {
  margin: 0 0 1.5rem 0;
  color: #333;
  font-size: 1.3rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #43a047;
}

.image-upload-buttons {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.image-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.image-container {
  position: relative;
  display: inline-block;
  margin-bottom: 0.5rem;
}

.preview-image {
  max-width: 200px;
  max-height: 150px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
  transition: all 0.2s ease;
}

.preview-image.clickable {
  cursor: pointer;
  border: 2px solid transparent;
}

.preview-image.clickable:hover {
  border-color: #43a047;
  box-shadow: 0 2px 6px rgba(67, 160, 71, 0.3);
  transform: scale(1.1);
}

.delete-photo-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.delete-photo-btn:hover {
  background: rgba(220, 53, 69, 1);
}

.small-image-preview {
  display: inline-block;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.small-preview-image {
  max-width: 120px;
  max-height: 90px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.small-preview-image:hover {
  border-color: #43a047;
  box-shadow: 0 2px 6px rgba(67, 160, 71, 0.3);
  transform: scale(1.05);
}
</style>